import { useQuery } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import useApplicationManagerProject from 'Hooks/queries/useApplicationManagerProject';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';

const creatorbaseUtils = creatorbaseQueries.project.utils;
const agencyUtils = creatorbaseQueries.agency.project.utils;

// hook is only enabled for creatorbase_round_planner group
export function useLinkedProject(projectId: number | null | undefined) {
    const hasLinkToCreatorbaseAccess = useCheckUserGroupsAccess(['creatorbase_round_planner']);
    const {
        data: appManagerProject,
        status: appManagerProjectStatus,
        error: appManagerProjectError,
        utils: appManagerUtils,
    } = useApplicationManagerProject(projectId, { enabled: !!hasLinkToCreatorbaseAccess });

    const creatorbaseProjectId = appManagerProject?.creatorbase_project_id;
    const isEnabled = !!creatorbaseProjectId && !!hasLinkToCreatorbaseAccess;
    // A mirrored project is owned by Round Agency: the client-surface endpoints hide it, so read
    // it and its team through the agency endpoints.
    const isMirroredToRoundAgency = !!appManagerProject?.is_mirrored_to_round_agency;

    const legacyProjectQuery = useQuery({
        ...creatorbaseQueries.project.options.detail(creatorbaseProjectId, { team_only: false }),
        enabled: isEnabled && !isMirroredToRoundAgency,
    });
    const agencyProjectQuery = useQuery({
        ...creatorbaseQueries.agency.project.options.detail(creatorbaseProjectId, { team_only: false }),
        enabled: isEnabled && isMirroredToRoundAgency,
    });
    const {
        data: linkedProject,
        status: linkedProjectStatus,
        error: linkedProjectError,
    } = isMirroredToRoundAgency ? agencyProjectQuery : legacyProjectQuery;

    const legacyTeamQuery = useQuery({
        ...creatorbaseQueries.team.options.detail(linkedProject?.team_id, { team_only: false }),
        enabled: !!linkedProject?.team_id && !!hasLinkToCreatorbaseAccess && !isMirroredToRoundAgency,
    });
    const agencyTeamQuery = useQuery({
        ...creatorbaseQueries.agency.team.options.detail(linkedProject?.team_id, { team_only: false }),
        enabled: !!linkedProject?.team_id && !!hasLinkToCreatorbaseAccess && isMirroredToRoundAgency,
    });
    const {
        data: team,
        status: teamStatus,
        error: teamError,
    } = isMirroredToRoundAgency ? agencyTeamQuery : legacyTeamQuery;

    const {
        data: brand,
        status: brandStatus,
        error: brandError,
    } = useQuery({
        ...creatorbaseQueries.brand.options.detail(linkedProject?.brand_id, { team_only: false }),
        enabled: !!linkedProject?.brand_id && !!hasLinkToCreatorbaseAccess,
    });

    const hasErrorLoadingSupplementaryData = !!teamError || !!brandError;

    return {
        appManagerProject,
        appManagerProjectStatus,
        appManagerProjectError: appManagerUtils.getErrorResponse(appManagerProjectError),
        linkedProject,
        linkedProjectStatus,
        linkedProjectError: isMirroredToRoundAgency
            ? agencyUtils.detail.getErrorResponse(linkedProjectError)
            : creatorbaseUtils.detail.getErrorResponse(linkedProjectError),
        isMirroredToRoundAgency,
        team,
        brand,

        isLinkedProjectLoading:
            appManagerProjectStatus === 'pending' ||
            (!!appManagerProject?.creatorbase_project_id && linkedProjectStatus === 'pending') ||
            (!!linkedProject?.brand_id && brandStatus === 'pending') ||
            (!!linkedProject?.team_id && teamStatus === 'pending'),
        hasErrorLoadingLinkedProject: !!appManagerProjectError || !!linkedProjectError,
        hasErrorLoadingSupplementaryData,
    };
}
